/**
 * Adjust percentages when one member's slider changes.
 *
 * Three behaviors:
 * - Total is 100% AND increasing: redistribute proportionally (take from others)
 * - Total is 100% AND decreasing: just decrease, don't touch others (frees up %)
 * - Total is not 100%: cap so total doesn't exceed 100% (no redistribution)
 *
 * Redistribution edge cases:
 * - All others at 0%: caps at 100 (no one to take from)
 * - Rounding: last member gets the remainder for exact 100% total
 */
export function redistributePercentages(
  changedUserId: string,
  newValue: number,
  currentPercentages: Record<string, number>
): Record<string, number> {
  const currentValue = currentPercentages[changedUserId] || 0;
  const isIncreasing = newValue > currentValue;

  const currentTotal = Object.values(currentPercentages).reduce(
    (sum, pct) => sum + (pct || 0),
    0
  );

  const otherEntries = Object.entries(currentPercentages).filter(
    ([id]) => id !== changedUserId
  );
  const othersTotal = otherEntries.reduce(
    (sum, [, pct]) => sum + (pct || 0),
    0
  );

  // Only redistribute when at 100% AND increasing
  if (Math.round(currentTotal) !== 100 || !isIncreasing) {
    const maxAllowed = Math.max(0, 100 - othersTotal);
    const cappedValue = Math.min(Math.max(0, newValue), maxAllowed);
    return { ...currentPercentages, [changedUserId]: cappedValue };
  }

  // At 100% and increasing - redistribute proportionally
  const clampedValue = Math.max(0, Math.min(100, newValue));
  const remainingForOthers = 100 - clampedValue;

  const result: Record<string, number> = { [changedUserId]: clampedValue };

  if (otherEntries.length === 0) {
    return result;
  }

  // All others at 0% - cap at 100 (no one to take from)
  if (othersTotal === 0) {
    otherEntries.forEach(([id]) => {
      result[id] = 0;
    });
    return result;
  }

  // Scale others proportionally, last member gets remainder
  let assigned = 0;
  otherEntries.forEach(([id, pct], index) => {
    if (index === otherEntries.length - 1) {
      result[id] = Math.max(0, remainingForOthers - assigned);
    } else {
      const scaled = Math.round(
        ((pct || 0) / othersTotal) * remainingForOthers
      );
      result[id] = Math.max(0, scaled);
      assigned += result[id];
    }
  });

  return result;
}
